import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { Plus, Trash2, Save, CheckCircle2, AlertCircle, Lock, ScrollText, RefreshCw, Pencil } from 'lucide-react';
import { fetchReviewRules, saveReviewRules, type ReviewRule } from '../lib/data';
import { useOrg } from '../lib/org-context';
import { DataTable, type Column, type FilterConfig, type RowAction } from './DataTable';
import { Modal } from './Modal';

const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'];
const CATEGORIES = ['security', 'correctness', 'performance', 'test', 'style', 'docs', 'other'];

const DEFAULT_RULES: ReviewRule[] = [
  {
    id: 'rule-secrets',
    name: 'No secrets in source',
    description: 'Reject hardcoded tokens, keys, and credentials.',
    severity: 'high',
    category: 'security',
    enabled: true,
    body: 'Scan the diff for API keys, private keys, passwords, and connection strings. Flag any secret committed to source and require it be moved to an environment variable or secret store.',
    createdAt: '',
    updatedAt: '',
  },
  {
    id: 'rule-sql-injection',
    name: 'Block injection vectors',
    description: 'Catch SQL/command injection and XSS.',
    severity: 'high',
    category: 'security',
    enabled: true,
    body: 'Flag string-concatenated SQL, unescaped HTML interpolation, and use of eval/exec on untrusted input. Require parameterized queries and proper output encoding.',
    createdAt: '',
    updatedAt: '',
  },
  {
    id: 'rule-require-tests',
    name: 'Require tests for new logic',
    description: 'New branches and functions need coverage.',
    severity: 'medium',
    category: 'test',
    enabled: true,
    body: 'If the PR adds a new function or branch, request a unit test covering the happy path and at least one edge case. Do not block refactors that only move code.',
    createdAt: '',
    updatedAt: '',
  },
  {
    id: 'rule-naming',
    name: 'Consistent naming',
    description: 'camelCase for JS/TS, snake_case for SQL.',
    severity: 'low',
    category: 'style',
    enabled: false,
    body: 'Prefer existing project conventions. camelCase for TS/JS identifiers, UPPER_SNAKE for SQL constants. Surface as a nit, not a blocker.',
    createdAt: '',
    updatedAt: '',
  },
];

/** Severity → text color, matching the Issues page tone scale. */
function severityFg(sev: string): string {
  switch (sev) {
    case 'critical':
    case 'high':
      return '#dc2626';
    case 'medium':
      return '#b45309';
    default:
      return 'var(--accent)';
  }
}

const fieldCls = 'w-full rounded-md px-2.5 py-1.5 text-xs focus:outline-none';
const fieldStyle: CSSProperties = { background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' };

function fmtDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

export function Rules() {
  const [rules, setRules] = useState<ReviewRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  // Rule editor modal: holds a draft copy so keystrokes don't re-render the list.
  // `draft` is the in-progress rule; `isNew` marks an Add vs an Edit.
  const [draft, setDraft] = useState<ReviewRule | null>(null);
  const [isNew, setIsNew] = useState(false);

  const { current } = useOrg();
  const orgId = current?.orgId ?? null;
  const canEdit = current?.role === 'OrgAdmin';

  // Capture the org in view so async writes (seed/reset) don't land in the
  // wrong org if the user switches mid-flight.
  const activeOrgRef = useRef<string | null>(null);
  useEffect(() => { activeOrgRef.current = orgId; }, [orgId]);

  const apply = (next: ReviewRule[]) => setRules(next);

  const applyAndPersist = async (next: ReviewRule[], oid: string) => {
    await saveReviewRules(next, oid);
    if (activeOrgRef.current === oid) apply(next);
  };

  // Load + first-load seed. Keyed on [orgId, canEdit] so switching orgs refetches.
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLoading(true);
    setSaveError(null);
    // Close any open editor — its draft belongs to the previous org, and
    // committing it after the refetch would cross-contaminate the new org.
    setDraft(null);
    setIsNew(false);
    (async () => {
      try {
        const data = await fetchReviewRules(orgId);
        if (cancelled) return;
        if (data.length === 0 && canEdit) {
          // Stamp createdAt on first seed (the DEFAULT_RULES placeholders ship empty)
          // so the rows always carry a real ISO timestamp.
          const stamped = DEFAULT_RULES.map((r) => ({ ...r, createdAt: new Date().toISOString() }));
          await applyAndPersist(stamped, orgId);
        } else if (data.length > 0) {
          if (!cancelled) apply(data);
        }
      } catch {
        // Worker offline (local dev) — fall back to defaults in-memory only.
        if (!cancelled) apply(DEFAULT_RULES);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, canEdit]);

  const patch = (id: string, p: Partial<ReviewRule>) =>
    setRules((rs) => rs.map((r) => (r.id === id ? { ...r, ...p } : r)));

  const openEdit = (r: ReviewRule) => { setDraft({ ...r }); setIsNew(false); };

  const openAdd = () => {
    setDraft({
      id: `rule-${Math.random().toString(36).slice(2, 10)}`,
      name: '',
      description: '',
      severity: 'medium',
      category: 'other',
      enabled: true,
      body: '',
      createdAt: '',
      updatedAt: '',
    });
    setIsNew(true);
  };

  const commitDraft = () => {
    if (!draft) return;
    const stamped = { ...draft, updatedAt: new Date().toISOString(), createdAt: draft.createdAt || new Date().toISOString() };
    if (isNew) setRules((rs) => [...rs, stamped]);
    else patch(stamped.id, stamped);
    setDraft(null);
  };

  const removeRule = (id: string) => setRules((rs) => rs.filter((r) => r.id !== id));

  const resetDefaults = async () => {
    if (!orgId) return;
    setIsResetting(true);
    try {
      await applyAndPersist(DEFAULT_RULES.map((r) => ({ ...r, createdAt: r.createdAt || new Date().toISOString() })), orgId);
    } catch (e: any) {
      setSaveError(e?.message ?? 'Reset failed.');
    } finally {
      setIsResetting(false);
    }
  };

  const handleSave = async () => {
    if (!orgId || loading) return;
    setSaveError(null);
    try {
      await saveReviewRules(rules, orgId);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (e: any) {
      setSaveError(e?.message ?? 'Save failed.');
    }
  };

  if (!orgId) {
    return <div className="py-10 text-center text-xs" style={{ color: 'var(--ink-secondary)' }}>Select an organization to manage its rules.</div>;
  }

  const columns: Column<ReviewRule>[] = [
    {
      key: 'rule',
      header: 'Rule',
      width: '46%',
      render: (r) => (
        <div className="space-y-0.5">
          <div className="font-medium" style={{ color: 'var(--ink)' }}>{r.name || <span style={{ color: 'var(--ink-secondary)' }}>Untitled rule</span>}</div>
          {r.description && <div className="text-xs" style={{ color: 'var(--ink-secondary)' }}>{r.description}</div>}
        </div>
      ),
    },
    { key: 'category', header: 'Category', width: '14%', render: (r) => <span className="text-xs capitalize">{r.category}</span> },
    {
      key: 'severity',
      header: 'Severity',
      width: '12%',
      render: (r) => <span className="text-xs font-semibold capitalize" style={{ color: severityFg(r.severity) }}>{r.severity}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '14%',
      render: (r) => (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
          style={{ background: r.enabled ? 'rgba(16,185,129,0.12)' : 'var(--ink-faint)', color: r.enabled ? '#059669' : 'var(--ink-secondary)' }}
        >
          {r.enabled ? 'Enabled' : 'Disabled'}
        </span>
      ),
    },
    { key: 'updated', header: 'Updated', width: '14%', render: (r) => <span className="text-xs" style={{ color: 'var(--ink-secondary)' }}>{fmtDate(r.updatedAt || r.createdAt)}</span> },
  ];

  const filters: FilterConfig<ReviewRule>[] = [
    {
      key: 'category',
      label: 'Category',
      options: CATEGORIES.map((c) => ({ value: c, label: c })),
      predicate: (r, v) => r.category === v,
    },
    {
      key: 'severity',
      label: 'Severity',
      options: SEVERITIES.map((s) => ({ value: s, label: s })),
      predicate: (r, v) => r.severity === v,
    },
    {
      key: 'status',
      label: 'Status',
      options: [{ value: 'enabled', label: 'Enabled' }, { value: 'disabled', label: 'Disabled' }],
      predicate: (r, v) => (v === 'enabled' ? r.enabled : !r.enabled),
    },
  ];

  const actions: RowAction<ReviewRule>[] = [
    { key: 'edit', label: 'Edit rule', icon: Pencil, onClick: openEdit, disabled: () => !canEdit, title: 'Edit' },
    { key: 'delete', label: 'Delete rule', icon: Trash2, onClick: (r) => removeRule(r.id), disabled: () => !canEdit, tone: 'danger', title: 'Delete' },
  ];

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        {!canEdit && (
          <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md" style={{ color: 'var(--ink-secondary)', background: 'var(--ink-faint)' }}>
            <Lock className="w-3 h-3" /> Read-only — OrgAdmin required to edit.
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {saveSuccess && (
            <span className="inline-flex items-center gap-1 text-xs" style={{ color: '#059669' }}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Saved
            </span>
          )}
          {saveError && (
            <span className="inline-flex items-center gap-1 text-xs" style={{ color: '#dc2626' }}>
              <AlertCircle className="w-3.5 h-3.5" /> {saveError}
            </span>
          )}
          <button
            onClick={resetDefaults}
            disabled={!canEdit || isResetting}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border disabled:opacity-50"
            style={{ borderColor: 'var(--border)', color: 'var(--ink-secondary)' }}
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reset defaults
          </button>
          <button
            onClick={openAdd}
            disabled={!canEdit}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border disabled:opacity-50"
            style={{ borderColor: 'var(--border)', color: 'var(--ink)' }}
          >
            <Plus className="w-3.5 h-3.5" /> Add rule
          </button>
          <button
            onClick={handleSave}
            disabled={!canEdit || loading}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-semibold disabled:opacity-50"
            style={{ background: 'var(--accent)', color: '#fff' }}
          >
            <Save className="w-3.5 h-3.5" /> Save
          </button>
        </div>
      </div>

      <DataTable
        rows={rules}
        columns={columns}
        rowKey={(r) => r.id}
        loading={loading}
        searchKeys={[(r) => r.name, (r) => r.description, (r) => r.body]}
        searchPlaceholder="Search rules…"
        filters={filters}
        actions={actions}
        pageSize={10}
        skeletonCols={['46%', '14%', '12%', '14%', '14%']}
        emptyState={
          <>
            <ScrollText className="w-5 h-5 mx-auto mb-2 opacity-60" />
            No rules. Click <strong>Add rule</strong> or <strong>Reset defaults</strong>.
          </>
        }
      />

      <p className="text-[10px]" style={{ color: 'var(--ink-secondary)' }}>
        Rules are stored per organization. They power the Issues + Reliability categorization and are ready to be wired into the review prompt.
      </p>

      {/* Editor modal — Add / Edit share the same form, writing to the draft on save. */}
      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={isNew ? 'Add rule' : 'Edit rule'}
        subtitle="Changes apply to the draft; click Save to persist."
        size="md"
        footer={
          <>
            <button
              onClick={() => setDraft(null)}
              className="text-xs px-3 py-1.5 rounded-md border"
              style={{ borderColor: 'var(--border)', color: 'var(--ink-secondary)' }}
            >
              Cancel
            </button>
            <button
              onClick={commitDraft}
              disabled={!draft?.name.trim()}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-semibold disabled:opacity-50"
              style={{ background: 'var(--accent)', color: '#fff' }}
            >
              {isNew ? <Plus className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {isNew ? 'Add' : 'Apply'}
            </button>
          </>
        }
      >
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--ink-secondary)' }}>Name</label>
              <input
                className={fieldCls}
                style={fieldStyle}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. No secrets in source"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--ink-secondary)' }}>Description</label>
              <input
                className={fieldCls}
                style={fieldStyle}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Short summary"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--ink-secondary)' }}>Severity</label>
                <select
                  className={fieldCls}
                  style={{ ...fieldStyle, color: severityFg(draft.severity), fontWeight: 600 }}
                  value={draft.severity}
                  onChange={(e) => setDraft({ ...draft, severity: e.target.value })}
                >
                  {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--ink-secondary)' }}>Category</label>
                <select
                  className={fieldCls}
                  style={fieldStyle}
                  value={draft.category}
                  onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--ink-secondary)' }}>Prompt body</label>
              <textarea
                rows={5}
                className={`${fieldCls} resize-y text-[11px]`}
                style={fieldStyle}
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                placeholder="Instruction text fed to the review agent."
              />
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: 'var(--ink)' }}>
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                className="accent-[var(--accent)]"
              />
              Enabled
            </label>
          </div>
        )}
      </Modal>
    </div>
  );
}
